import type {
  ExamSectionDraft,
  ExamSectionId,
  HireHealthStep,
  SectionId,
  TextSectionDraft,
  TextSectionId,
} from './hireHealthSurveyTypes';
import { stepIndex, stepQuestionKey } from './hireHealthSurveySteps';

export { stepQuestionKey };

function isExamSection(id: SectionId): id is ExamSectionId {
  return id !== 'aftereffects' && id !== 'symptoms';
}

function gateStep(sectionId: SectionId): HireHealthStep {
  return `${sectionId}_gate` as HireHealthStep;
}

function dateStep(sectionId: ExamSectionId): HireHealthStep {
  return `${sectionId}_date` as HireHealthStep;
}

function hospitalStep(sectionId: ExamSectionId): HireHealthStep {
  return `${sectionId}_hospital` as HireHealthStep;
}

function detailStep(sectionId: TextSectionId): HireHealthStep {
  return `${sectionId}_detail` as HireHealthStep;
}

/** Steps already answered in the active section (shown as Q&A in the thread). */
export function getCommittedStepsForSection(
  sectionId: SectionId,
  currentStep: HireHealthStep,
  examDraft: ExamSectionDraft,
  textDraft: TextSectionDraft
): HireHealthStep[] {
  const gate = gateStep(sectionId);
  const out: HireHealthStep[] = [];

  if (isExamSection(sectionId)) {
    if (!examDraft.hadExam) return out;
    out.push(gate);
    if (examDraft.hadExam === 'no') return out;

    const d = dateStep(sectionId);
    const h = hospitalStep(sectionId);
    const f = `${sectionId}_fit` as HireHealthStep;
    if (stepIndex(currentStep) > stepIndex(d)) out.push(d);
    if (stepIndex(currentStep) > stepIndex(h)) out.push(h);
    if (stepIndex(currentStep) > stepIndex(f) || (currentStep === f && examDraft.fit)) {
      out.push(f);
    }
    return out;
  }

  if (!textDraft.hadIssue) return out;
  out.push(gate);
  if (textDraft.hadIssue === 'no') return out;

  const det = detailStep(sectionId);
  if (stepIndex(currentStep) > stepIndex(det)) out.push(det);
  return out;
}

export function userAnswerForStep(
  step: HireHealthStep,
  sectionId: SectionId,
  examDraft: ExamSectionDraft,
  textDraft: TextSectionDraft,
  t: (key: string) => string
): string {
  if (step.endsWith('_gate')) {
    if (isExamSection(sectionId)) {
      return examDraft.hadExam === 'yes'
        ? t('submitDocuments.hireHealthSurvey.yes')
        : t('submitDocuments.hireHealthSurvey.no');
    }
    if (textDraft.hadIssue === 'yes') return t('submitDocuments.hireHealthSurvey.yes');
    return sectionId === 'symptoms'
      ? t('submitDocuments.hireHealthSurvey.noSymptoms')
      : t('submitDocuments.hireHealthSurvey.no');
  }
  if (step.endsWith('_date')) {
    return examDraft.examDate.replace(/-/g, '/');
  }
  if (step.endsWith('_hospital')) {
    return examDraft.hospital.trim();
  }
  if (step.endsWith('_fit')) {
    if (examDraft.fit === 'fit') return t('submitDocuments.hireHealthSurvey.fit');
    if (examDraft.fit === 'not_fit') return t('submitDocuments.hireHealthSurvey.notFit');
    return '';
  }
  if (step.endsWith('_detail')) {
    return textDraft.detail.trim();
  }
  return '';
}

/** After undoing a completed summary, reopen at the last step of that section. */
export function resumeStepForSectionSnapshot(
  sectionId: SectionId,
  examDraft: ExamSectionDraft,
  textDraft: TextSectionDraft
): HireHealthStep {
  if (isExamSection(sectionId)) {
    if (examDraft.hadExam === 'no') return gateStep(sectionId);
    return `${sectionId}_fit` as HireHealthStep;
  }
  if (textDraft.hadIssue === 'no') return gateStep(sectionId);
  return detailStep(sectionId);
}

export function stateAfterUndoLastCommitted(
  sectionId: SectionId,
  lastCommitted: HireHealthStep,
  examDraft: ExamSectionDraft,
  textDraft: TextSectionDraft
): { step: HireHealthStep; examDraft: ExamSectionDraft; textDraft: TextSectionDraft } {
  if (lastCommitted.endsWith('_gate')) {
    if (isExamSection(sectionId)) {
      return { step: gateStep(sectionId), examDraft: { hadExam: '', examDate: '', hospital: '', fit: '' }, textDraft };
    }
    return { step: gateStep(sectionId), examDraft, textDraft: { hadIssue: '', detail: '' } };
  }

  if (lastCommitted.endsWith('_date') && isExamSection(sectionId)) {
    return {
      step: dateStep(sectionId),
      examDraft: { ...examDraft, examDate: '', hospital: '', fit: '' },
      textDraft,
    };
  }

  if (lastCommitted.endsWith('_hospital') && isExamSection(sectionId)) {
    return {
      step: hospitalStep(sectionId),
      examDraft: { ...examDraft, hospital: '', fit: '' },
      textDraft,
    };
  }

  if (lastCommitted.endsWith('_fit') && isExamSection(sectionId)) {
    return {
      step: `${sectionId}_fit` as HireHealthStep,
      examDraft: { ...examDraft, fit: '' },
      textDraft,
    };
  }

  if (lastCommitted.endsWith('_detail')) {
    return {
      step: detailStep(sectionId as TextSectionId),
      examDraft,
      textDraft: { ...textDraft, detail: '' },
    };
  }

  return { step: lastCommitted, examDraft, textDraft };
}
